import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import { useSettings } from "@/components/layout/PublicLayout";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import SpeakerAvatar from "@/components/shared/SpeakerAvatar";
import { registrationCta, editionMonthYear, previousRealEdition, homeMode } from "@/lib/useEditions";
import { ArrowRight, ArrowUpRight, MapPin, Calendar } from "lucide-react";

const Counter = ({ value }) => {
  const num = parseInt((value || "").replace(/\D/g, ""), 10);
  const [display, setDisplay] = useState(isNaN(num) ? value : "0");
  useEffect(() => {
    if (isNaN(num)) { setDisplay(value); return; }
    let start = 0;
    const dur = 1400, step = 20;
    const inc = num / (dur / step);
    const t = setInterval(() => {
      start += inc;
      if (start >= num) { setDisplay(value); clearInterval(t); }
      else setDisplay(Math.floor(start).toLocaleString("fr-FR"));
    }, step);
    return () => clearInterval(t);
  }, [num, value]);
  return <span>{display}</span>;
};

const Home = () => {
  const settings = useSettings();
  const hero = settings?.hero || {};
  const metrics = settings?.key_metrics || [];
  const next = settings?.next_edition || {};
  const about = settings?.about || {};
  const ptx = settings?.pages?.home || {};

  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const { data: articles = [] } = useQuery({ queryKey: ["articles"], queryFn: async () => (await api.get("/articles")).data });
  const { data: speakers = [] } = useQuery({ queryKey: ["speakers"], queryFn: async () => (await api.get("/speakers")).data });
  const nextEd = editions.find((e) => e.status === "upcoming");
  const featured = editions.find((e) => e.is_active) || editions.find((e) => e.status === "active") || nextEd;
  const mode = homeMode(featured);
  const cta = registrationCta(featured, nextEd);
  const my = editionMonthYear(featured);
  const edYear = (id) => editions.find((e) => e.id === id)?.year;
  const homeArticles = [...articles].filter((a) => a.featured_home).sort((a, b) => (a.home_order || 0) - (b.home_order || 0)).slice(0, 3);

  const ed2026 = featured;
  const heroImg = mediaUrl(featured?.hero_image || settings?.seo?.og_image);
  const prevEd = mode !== "after" ? previousRealEdition(editions, featured) : null;
  const prevImg = mediaUrl(prevEd?.hero_image || prevEd?.gallery?.[0]);

  return (
    <>
      <Seo title={settings?.seo?.default_title} description={settings?.seo?.default_description} image={settings?.seo?.og_image} />

      {/* HERO */}
      <section className="relative min-h-screen flex items-end bg-f2b-black overflow-hidden" data-testid="hero-section">
        {heroImg && (
          <img src={heroImg} alt="Façade architecturale contemporaine" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-f2b-black via-f2b-black/70 to-f2b-black/30" />
        <div className="f2b-container relative z-10 pb-24 pt-40 w-full">
          <Reveal>
            <p className="f2b-kicker text-f2b-cyan mb-6">{featured?.year ? `FACADES2build ${featured.year}` : (hero.kicker || "FACADES2build")}</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl">
              {featured?.theme || hero.title || "Rénovation — Réhabilitation des façades"}
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="f2b-lead text-white/80 text-xl md:text-2xl mt-6">
              {mode === "after"
                ? `Retour sur l'édition du ${featured?.date_label || my}`
                : `${my || featured?.date_label || ""}${featured?.city ? " · " + featured.city : ""}`}
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="text-white/70 max-w-xl mt-6 text-base leading-relaxed">{featured?.summary || hero.lead}</p>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="flex flex-wrap gap-4 mt-10">
              {mode === "after" ? (
                <>
                  <Link to={featured?.slug ? `/archives/${featured.slug}` : "/edition-2026"}>
                    <Btn variant="cyan" data-testid="hero-cta-primary">
                      {featured?.year ? `Découvrir l'édition ${featured.year}` : "Découvrir l'édition"} <ArrowRight size={18} />
                    </Btn>
                  </Link>
                  <Link to="/blog"><Btn variant="outlineLight" data-testid="hero-cta-secondary">Voir les expertises</Btn></Link>
                </>
              ) : (
                <>
                  {cta.show && (cta.external ? (
                    <a href={cta.to} target="_blank" rel="noreferrer"><Btn variant="cyan" data-testid="hero-cta-primary">{cta.label} <ArrowRight size={18} /></Btn></a>
                  ) : (
                    <Link to={cta.to}><Btn variant="cyan" data-testid="hero-cta-primary">{cta.label} <ArrowRight size={18} /></Btn></Link>
                  ))}
                  <Link to="/programme"><Btn variant="outlineLight" data-testid="hero-cta-secondary">Voir le programme</Btn></Link>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PREVIOUS EDITION RETROSPECTIVE (before mode only) */}
      {mode !== "after" && prevEd && (
        <section className="py-24 bg-white" data-testid="previous-edition-retrospective">
          <div className="f2b-container grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div className="relative aspect-[4/3] overflow-hidden bg-f2b-light">
                {prevImg && (
                  <img src={prevImg} alt={`FACADES2build ${prevEd.year || ""}`} className="w-full h-full object-cover" />
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="f2b-kicker text-f2b-cyan mb-4">Rétrospective</p>
              <h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl leading-tight">
                {prevEd.year ? `Retour sur l'édition ${prevEd.year}` : "Retour sur la précédente édition"}
              </h2>
              <div className="flex flex-wrap gap-5 mt-6 text-sm text-f2b-gray">
                <span className="flex items-center gap-2"><Calendar size={16} className="text-f2b-cyan" /> {prevEd.date_label || editionMonthYear(prevEd)}</span>
                {prevEd.venue && <span className="flex items-center gap-2"><MapPin size={16} className="text-f2b-cyan" /> {prevEd.venue}</span>}
              </div>
              {prevEd.summary && <p className="text-f2b-text/80 mt-6 leading-relaxed line-clamp-4">{prevEd.summary}</p>}
              <div className="mt-8">
                <Link to={prevEd.slug ? `/archives/${prevEd.slug}` : "/archives"}>
                  <Btn variant="dark" data-testid="previous-edition-cta">
                    {prevEd.year ? `Revivre l'édition ${prevEd.year}` : "Revivre l'édition"} <ArrowRight size={16} />
                  </Btn>
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* KEY METRICS */}
      <section className="bg-f2b-black border-t border-white/10 py-16" data-testid="metrics-section">
        <div className="f2b-container grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
          {metrics.map((m, i) => (
            <Reveal key={i} delay={i * 0.08} className="border-l-2 border-f2b-cyan pl-5">
              <div className="font-display font-bold text-white text-4xl md:text-5xl"><Counter value={m.value} /></div>
              <div className="text-f2b-gray text-sm mt-2 uppercase tracking-wider">{m.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDITION RETROSPECTIVE (after mode only) */}
      {mode === "after" && (
      <section className="py-28 bg-white" data-testid="edition-2026-retrospective">
        <div className="f2b-container grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden">
              {mediaUrl(ed2026?.gallery?.[0] || heroImg) && (
                <img src={mediaUrl(ed2026?.gallery?.[0] || heroImg)} alt={`Conférence FACADES2build ${ed2026?.year || ""}`} className="w-full h-full object-cover" />
              )}
              <div className="absolute bottom-0 left-0 bg-f2b-cyan text-f2b-black px-6 py-4">
                <span className="font-display font-bold text-2xl">{ed2026?.date_label}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="f2b-kicker text-f2b-cyan mb-4">Édition terminée · Rétrospective</p>
            <h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl leading-tight">
              {ed2026?.theme || "Rénovation — Réhabilitation des façades"}
            </h2>
            <div className="flex flex-wrap gap-5 mt-6 text-sm text-f2b-gray">
              <span className="flex items-center gap-2"><Calendar size={16} className="text-f2b-cyan" /> {ed2026?.date_label}</span>
              {ed2026?.venue && <span className="flex items-center gap-2"><MapPin size={16} className="text-f2b-cyan" /> {ed2026.venue}</span>}
            </div>
            <p className="text-f2b-text/80 mt-6 leading-relaxed">{ed2026?.summary}</p>
            <div className="flex gap-4 mt-8">
              <Link to={ed2026?.slug ? `/archives/${ed2026.slug}` : "/edition-2026"}><Btn variant="dark" data-testid="home-cta-edition">{ed2026?.year ? `Revivre l'édition ${ed2026.year}` : "Revivre l'édition"} <ArrowRight size={16} /></Btn></Link>
              <Link to="/programme"><Btn variant="outlineDark">Voir le programme</Btn></Link>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* POSITIONING */}
      <section className="py-24 bg-f2b-light" data-testid="positioning-section">
        <div className="f2b-container max-w-3xl">
          <Reveal>
            <p className="f2b-kicker text-f2b-gray mb-4">L'événement</p>
            <h2 className="font-display font-bold text-f2b-black text-3xl md:text-5xl leading-tight">
              {about.title || "FACADES2build n'est pas un salon."}
            </h2>
            <p className="text-f2b-text/80 mt-8 text-lg leading-relaxed">{about.text}</p>
          </Reveal>
        </div>
      </section>

      {/* SPEAKERS PREVIEW */}
      <section className="py-28 bg-white" data-testid="speakers-preview">
        <div className="f2b-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <Reveal>
              <p className="f2b-kicker text-f2b-cyan mb-3">{ptx.speakers_kicker || (featured?.year ? `Intervenants ${featured.year}` : "Intervenants")}</p>
              <h2 className="font-display font-bold text-f2b-black text-3xl md:text-4xl">{ptx.speakers_title || "Les experts qui ont construit le programme"}</h2>
            </Reveal>
            <Link to="/intervenants" className="f2b-link-underline text-f2b-black text-sm flex items-center gap-1">
              Tous les intervenants <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {speakers.filter((sp) => !featured || (sp.edition_ids || []).includes(featured.id)).slice(0, 6).map((sp, i) => (
              <Reveal key={sp.id} delay={i * 0.05}>
                <Link to={`/intervenants/${sp.slug}`} className="group block" data-testid={`home-speaker-${i}`}>
                  <div className="aspect-square overflow-hidden mb-3">
                    <div className="w-full h-full group-hover:scale-105 transition-transform duration-500">
                      <SpeakerAvatar speaker={sp} />
                    </div>
                  </div>
                  <p className="font-display font-bold text-f2b-black text-base leading-tight">{sp.name}</p>
                  <p className="text-f2b-gray text-xs mt-1">{sp.company}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ARTICLES — only published articles flagged "featured on Home" */}
      {homeArticles.length > 0 && (
      <section className="py-28 bg-f2b-black" data-testid="articles-preview">
        <div className="f2b-container">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <Reveal>
              <p className="f2b-kicker text-f2b-cyan mb-3">{ptx.articles_kicker || "Expertises"}</p>
              <h2 className="font-display font-bold text-white text-3xl md:text-4xl">{ptx.articles_title || "Retours d'expérience & articles"}</h2>
            </Reveal>
            <Link to="/blog" className="f2b-link-underline text-white text-sm flex items-center gap-1">
              Toutes les expertises <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {homeArticles.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.08}>
                <Link to={`/blog/${a.slug}`} className="group block" data-testid={`home-article-${i}`}>
                  <div className="aspect-[3/2] overflow-hidden mb-5">
                    {mediaUrl(a.image) && <img src={mediaUrl(a.image)} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="f2b-kicker text-f2b-cyan">{a.theme}</span>
                    {edYear(a.edition_id) && <span className="text-white/45 text-xs uppercase tracking-wider">· Édition {edYear(a.edition_id)}</span>}
                  </div>
                  <h3 className="font-display font-bold text-white text-xl mt-3 leading-tight group-hover:text-f2b-cyan transition-colors">{a.title}</h3>
                  <p className="text-white/60 text-sm mt-3 line-clamp-3">{a.chapo}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* NEXT EDITION — hidden when no upcoming edition exists */}
      {nextEd && (
      <section className="py-24 bg-f2b-cyan" data-testid="next-edition-section">
        <div className="f2b-container flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="f2b-kicker text-f2b-black/70 mb-3">Prochaine édition</p>
            <h2 className="font-display font-bold text-f2b-black text-4xl md:text-5xl">{next.title || (nextEd ? `Prochaine édition — ${editionMonthYear(nextEd)}` : "Prochaine édition")}</h2>
            <p className="text-f2b-black/80 mt-4 max-w-xl">{next.text || nextEd?.summary || ""}</p>
          </div>
          {cta.show && (cta.external ? (
            <a href={cta.to} target="_blank" rel="noreferrer">
              <Btn variant="dark" data-testid="next-edition-cta">{cta.label} <ArrowRight size={18} /></Btn>
            </a>
          ) : (
            <Link to={cta.to}>
              <Btn variant="dark" data-testid="next-edition-cta">{cta.label} <ArrowRight size={18} /></Btn>
            </Link>
          ))}
        </div>
      </section>
      )}
    </>
  );
};

export default Home;
